/**
 * Official Frostgrave Spell Data
 * Source: Frostgrave Spell Cards & Quick Reference Sheet (public PDFs)
 *
 * Each school has 8 spells. Casting numbers range from 8-14.
 * Apprentices cast at +2 to the casting number.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SpellCategory =
  | 'Self Only'
  | 'Line of Sight'
  | 'Out of Game'
  | 'Touch'
  | 'Area Effect';

export interface Spell {
  id: string;
  name: string;
  school: WizardSchool;
  castingNumber: number;
  category: SpellCategory;
  description: string;
}

export type WizardSchool =
  | 'Chronomancer'
  | 'Elementalist'
  | 'Enchanter'
  | 'Illusionist'
  | 'Necromancer'
  | 'Sigilist'
  | 'Soothsayer'
  | 'Summoner'
  | 'Thaumaturge'
  | 'Witch';

// ---------------------------------------------------------------------------
// School Relationships (for aligned/neutral/opposed)
// ---------------------------------------------------------------------------

export const SCHOOL_RELATIONSHIPS: Record<
  WizardSchool,
  { aligned: WizardSchool[]; neutral: WizardSchool[]; opposed: WizardSchool }
> = {
  Chronomancer: {
    aligned: ['Necromancer', 'Soothsayer', 'Elementalist'],
    neutral: ['Illusionist', 'Sigilist', 'Summoner', 'Thaumaturge', 'Witch'],
    opposed: 'Enchanter',
  },
  Elementalist: {
    aligned: ['Chronomancer', 'Enchanter', 'Summoner'],
    neutral: ['Illusionist', 'Necromancer', 'Sigilist', 'Soothsayer', 'Witch'],
    opposed: 'Thaumaturge',
  },
  Enchanter: {
    aligned: ['Elementalist', 'Sigilist', 'Witch'],
    neutral: ['Illusionist', 'Necromancer', 'Soothsayer', 'Summoner', 'Thaumaturge'],
    opposed: 'Chronomancer',
  },
  Illusionist: {
    aligned: ['Sigilist', 'Soothsayer', 'Thaumaturge'],
    neutral: ['Chronomancer', 'Elementalist', 'Enchanter', 'Necromancer', 'Witch'],
    opposed: 'Summoner',
  },
  Necromancer: {
    aligned: ['Chronomancer', 'Summoner', 'Witch'],
    neutral: ['Elementalist', 'Enchanter', 'Illusionist', 'Sigilist', 'Soothsayer'],
    opposed: 'Thaumaturge',
  },
  Sigilist: {
    aligned: ['Enchanter', 'Illusionist', 'Thaumaturge'],
    neutral: ['Chronomancer', 'Elementalist', 'Necromancer', 'Soothsayer', 'Witch'],
    opposed: 'Summoner',
  },
  Soothsayer: {
    aligned: ['Chronomancer', 'Illusionist', 'Thaumaturge'],
    neutral: ['Elementalist', 'Enchanter', 'Necromancer', 'Sigilist', 'Witch'],
    opposed: 'Witch',
  },
  Summoner: {
    aligned: ['Elementalist', 'Necromancer', 'Witch'],
    neutral: ['Chronomancer', 'Enchanter', 'Soothsayer', 'Thaumaturge'],
    opposed: 'Sigilist',
  },
  Thaumaturge: {
    aligned: ['Illusionist', 'Sigilist', 'Soothsayer'],
    neutral: ['Chronomancer', 'Enchanter', 'Summoner', 'Witch'],
    opposed: 'Necromancer',
  },
  Witch: {
    aligned: ['Enchanter', 'Necromancer', 'Summoner'],
    neutral: ['Chronomancer', 'Elementalist', 'Illusionist', 'Sigilist', 'Thaumaturge'],
    opposed: 'Soothsayer',
  },
};

// ---------------------------------------------------------------------------
// All Spells Data
// ---------------------------------------------------------------------------

export const SPELLS: Spell[] = [
  // =========================================================================
  // CHRONOMANCER
  // =========================================================================
  {
    id: 'chronomancer-crumble',
    name: 'Crumble',
    school: 'Chronomancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'This spell can only target inanimate structures such as walls and doors. The spellcaster rapidly ages the target, causing it to collapse. This spell can destroy a section of wall up to 2" wide and 6" tall.',
  },
  {
    id: 'chronomancer-decay',
    name: 'Decay',
    school: 'Chronomancer',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster may target any one item of equipment carried by an enemy figure. That equipment is destroyed. This includes magic weapons and armour, but not magic items that aren\'t carried. The target figure may make a Will roll (TN = casting roll); if successful, the spell has no effect.',
  },
  {
    id: 'chronomancer-fast-act',
    name: 'Fast Act',
    school: 'Chronomancer',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'This spell may only be cast on a member of the spellcaster\'s warband. This figure will activate next after the spellcaster, ahead of all other figures in the turn sequence.',
  },
  {
    id: 'chronomancer-fleet-feet',
    name: 'Fleet Feet',
    school: 'Chronomancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target figure gains +2 Move for the rest of the game. Multiple castings of this spell on the same target have no effect.',
  },
  {
    id: 'chronomancer-petrify',
    name: 'Petrify',
    school: 'Chronomancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target figure must make an immediate Will roll (TN = casting roll). If it fails, it is turned to stone and is removed from the game as a casualty.',
  },
  {
    id: 'chronomancer-slow',
    name: 'Slow',
    school: 'Chronomancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target must make a Will roll (TN = casting roll). If failed, the target suffers -2 Move and -2 to all die rolls for the rest of the turn.',
  },
  {
    id: 'chronomancer-time-store',
    name: 'Time Store',
    school: 'Chronomancer',
    castingNumber: 14,
    category: 'Self Only',
    description:
      'The spellcaster gains a stored action. At any point later in the game, the spellcaster may use this stored action to take one additional action during their activation (three actions total).',
  },
  {
    id: 'chronomancer-time-walk',
    name: 'Time Walk',
    school: 'Chronomancer',
    castingNumber: 14,
    category: 'Self Only',
    description:
      'The spellcaster immediately takes another full activation. The spellcaster cannot cast another spell during this activation.',
  },

  // =========================================================================
  // ELEMENTALIST
  // =========================================================================
  {
    id: 'elementalist-call-storm',
    name: 'Call Storm',
    school: 'Elementalist',
    castingNumber: 12,
    category: 'Area Effect',
    description:
      'All shooting attacks are at -1 for the rest of the game. This spell may be cast multiple times (by any spellcaster), each casting increasing the penalty by a further -1.',
  },
  {
    id: 'elementalist-destructive-sphere',
    name: 'Destructive Sphere',
    school: 'Elementalist',
    castingNumber: 12,
    category: 'Area Effect',
    description:
      'Every figure within 3" of the spellcaster (but not the spellcaster themselves) suffers a +5 attack.',
  },
  {
    id: 'elementalist-elemental-ball',
    name: 'Elemental Ball',
    school: 'Elementalist',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster selects a target point within 16". Every figure within 1.5" of that point suffers a +5 shooting attack.',
  },
  {
    id: 'elementalist-elemental-bolt',
    name: 'Elemental Bolt',
    school: 'Elementalist',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster makes a +7 shooting attack against any figure within line of sight.',
  },
  {
    id: 'elementalist-elemental-hammer',
    name: 'Elemental Hammer',
    school: 'Elementalist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'This spell is cast on a weapon. The next time the figure wielding this weapon attacks and wins a combat, it does +3 damage (in addition to any other bonuses). The spell is then lost. Multiple castings on the same weapon have no effect.',
  },
  {
    id: 'elementalist-elemental-shield',
    name: 'Elemental Shield',
    school: 'Elementalist',
    castingNumber: 10,
    category: 'Self Only',
    description:
      'The spellcaster gains +3 Armour for the rest of the game. Multiple castings have no effect.',
  },
  {
    id: 'elementalist-scatter-shot',
    name: 'Scatter Shot',
    school: 'Elementalist',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster makes a +0 shooting attack against every enemy figure within 12" and line of sight. This attack does not cause any damage, but any figure hit is knocked prone.',
  },
  {
    id: 'elementalist-wall',
    name: 'Wall',
    school: 'Elementalist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'This spell creates a wall of elemental energy 6" long, 3" tall and 1" thick anywhere within line of sight. The wall may not be created within 1" of any figure. Figures may not move through the wall, but attacks and spells may pass through. The wall lasts until the end of the game.',
  },

  // =========================================================================
  // ENCHANTER
  // =========================================================================
  {
    id: 'enchanter-animate-construct',
    name: 'Animate Construct',
    school: 'Enchanter',
    castingNumber: 10,
    category: 'Out of Game',
    description:
      'It is assumed the spellcaster has built a construct. This spell animates that construct, which can then be brought on missions. A spellcaster may have a maximum of one construct at a time.',
  },
  {
    id: 'enchanter-control-construct',
    name: 'Control Construct',
    school: 'Enchanter',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The target construct must make a Will roll (TN = casting roll). If failed, the spellcaster takes control of the construct for the rest of the game.',
  },
  {
    id: 'enchanter-embed-enchantment',
    name: 'Embed Enchantment',
    school: 'Enchanter',
    castingNumber: 14,
    category: 'Out of Game',
    description:
      'The spellcaster embeds one spell into an item, creating a single-use magic item. The embedded spell must be from the spellcaster\'s known spells.',
  },
  {
    id: 'enchanter-enchant-armour',
    name: 'Enchant Armour',
    school: 'Enchanter',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target figure gains +1 Armour for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'enchanter-enchant-weapon',
    name: 'Enchant Weapon',
    school: 'Enchanter',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target weapon gains +1 Fight for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'enchanter-grenade',
    name: 'Grenade',
    school: 'Enchanter',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster enchants an object (rock, bottle, etc.) so that when thrown it explodes with tremendous force. The spellcaster makes a +3 shooting attack against all figures within 1.5" of the target point.',
  },
  {
    id: 'enchanter-strength',
    name: 'Strength',
    school: 'Enchanter',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target figure gains +2 Fight for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'enchanter-telekinesis',
    name: 'Telekinesis',
    school: 'Enchanter',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster may move one treasure token up to 6" in any direction (including up). The treasure must remain in line of sight at all times, and may not be moved into contact with any figure.',
  },

  // =========================================================================
  // ILLUSIONIST
  // =========================================================================
  {
    id: 'illusionist-beauty',
    name: 'Beauty',
    school: 'Illusionist',
    castingNumber: 10,
    category: 'Self Only',
    description:
      'The spellcaster appears so beautiful that enemies find it difficult to attack. Any enemy figure wishing to attack the spellcaster must first make a Will roll (TN = casting roll). If failed, they may not attack the spellcaster this turn.',
  },
  {
    id: 'illusionist-fool-gold',
    name: 'Fool\'s Gold',
    school: 'Illusionist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster creates an illusion of a treasure token anywhere within line of sight. The first figure to move into contact with the token discovers it is fake and the token is removed.',
  },
  {
    id: 'illusionist-glow',
    name: 'Glow',
    school: 'Illusionist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target of this spell glows with a soft magical light. All shooting attacks against the target receive +3 for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'illusionist-illusionary-soldier',
    name: 'Illusionary Soldier',
    school: 'Illusionist',
    castingNumber: 12,
    category: 'Touch',
    description:
      'The spellcaster creates an illusionary soldier within 1". The soldier has Move 6, Fight +2, Shoot +0, Armour 10, Will +0, Health 1. It may not pick up treasure or leave the table. If it takes any damage, it is removed.',
  },
  {
    id: 'illusionist-invisibility',
    name: 'Invisibility',
    school: 'Illusionist',
    castingNumber: 12,
    category: 'Touch',
    description:
      'The target figure becomes invisible. It may not be attacked or targeted by spells. It may not carry treasure. The invisibility is cancelled if the figure attacks, casts a spell, or picks up treasure.',
  },
  {
    id: 'illusionist-monstrous-form',
    name: 'Monstrous Form',
    school: 'Illusionist',
    castingNumber: 10,
    category: 'Self Only',
    description:
      'The spellcaster takes on a terrifying appearance. Any figure wishing to move into combat with the spellcaster must first make a Will roll (TN = casting roll).',
  },
  {
    id: 'illusionist-teleport',
    name: 'Teleport',
    school: 'Illusionist',
    castingNumber: 10,
    category: 'Self Only',
    description:
      'The spellcaster immediately moves to any point within line of sight.',
  },
  {
    id: 'illusionist-transpose',
    name: 'Transpose',
    school: 'Illusionist',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster swaps positions with any friendly figure within line of sight. If an unwilling target is transposed (enemy or unwilling friendly), they may make a Will roll (TN = casting roll) to resist.',
  },

  // =========================================================================
  // NECROMANCER
  // =========================================================================
  {
    id: 'necromancer-bone-dart',
    name: 'Bone Dart',
    school: 'Necromancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster makes a +5 shooting attack against any figure within line of sight.',
  },
  {
    id: 'necromancer-bones-of-the-earth',
    name: 'Bones of the Earth',
    school: 'Necromancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'A skeletal hand rises from the ground to grab the target. The target must make a Will roll (TN = casting roll) or be unable to take any actions this turn. If engaged in combat, they fight with -4 Fight.',
  },
  {
    id: 'necromancer-control-undead',
    name: 'Control Undead',
    school: 'Necromancer',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The target undead creature must make a Will roll (TN = casting roll). If failed, the creature becomes a member of the spellcaster\'s warband for the rest of the game.',
  },
  {
    id: 'necromancer-raise-zombie',
    name: 'Raise Zombie',
    school: 'Necromancer',
    castingNumber: 10,
    category: 'Touch',
    description:
      'The spellcaster animates a corpse as a zombie. The zombie is placed within 1" of the spellcaster. A spellcaster may have a maximum of three zombies active at once.',
  },
  {
    id: 'necromancer-spell-eater',
    name: 'Spell Eater',
    school: 'Necromancer',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'Cancels any one spell currently in effect. The spellcaster does not need line of sight to the original caster, only to the target of the spell being cancelled.',
  },
  {
    id: 'necromancer-steal-health',
    name: 'Steal Health',
    school: 'Necromancer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target must make a Will roll (TN = casting roll). If failed, the target takes 3 damage and the spellcaster heals 3 Health (up to their maximum).',
  },
  {
    id: 'necromancer-strike-dead',
    name: 'Strike Dead',
    school: 'Necromancer',
    castingNumber: 18,
    category: 'Line of Sight',
    description:
      'The target must make a Will roll (TN = casting roll). If failed, the target is killed immediately. If passed, the target takes 5 damage instead.',
  },
  {
    id: 'necromancer-summon-undead',
    name: 'Summon Undead',
    school: 'Necromancer',
    castingNumber: 12,
    category: 'Touch',
    description:
      'The spellcaster summons an undead creature (skeleton, ghoul, or armoured skeleton) to appear within 1". The creature is a member of the warband for the rest of the game.',
  },

  // =========================================================================
  // SIGILIST
  // =========================================================================
  {
    id: 'sigilist-absorb-knowledge',
    name: 'Absorb Knowledge',
    school: 'Sigilist',
    castingNumber: 12,
    category: 'Out of Game',
    description:
      'The spellcaster studies the writings of another wizard. The spellcaster gains 40 experience points.',
  },
  {
    id: 'sigilist-create-grimoire',
    name: 'Create Grimoire',
    school: 'Sigilist',
    castingNumber: 14,
    category: 'Out of Game',
    description:
      'The spellcaster creates a Grimoire of one spell they know. This Grimoire may be sold, given to an apprentice, or used to teach the spell to another wizard.',
  },
  {
    id: 'sigilist-draining-word',
    name: 'Draining Word',
    school: 'Sigilist',
    castingNumber: 14,
    category: 'Area Effect',
    description:
      'The spellcaster selects one spell. Every time that spell is cast for the rest of the game, the casting number is increased by 3. A spellcaster may only have one Draining Word in effect at a time.',
  },
  {
    id: 'sigilist-explosive-rune',
    name: 'Explosive Rune',
    school: 'Sigilist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster draws a rune on a surface within line of sight. The first figure to move within 1" of the rune triggers it and suffers a +5 attack. Runes remain until triggered or the game ends.',
  },
  {
    id: 'sigilist-furious-quill',
    name: 'Furious Quill',
    school: 'Sigilist',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target is attacked by an animated quill. The target suffers -1 Move, -2 Fight, and -4 Shoot while the quill attacks. The target may use an action to swat the quill (automatic success).',
  },
  {
    id: 'sigilist-power-word',
    name: 'Power Word',
    school: 'Sigilist',
    castingNumber: 14,
    category: 'Area Effect',
    description:
      'The spellcaster selects one spell. Every time that spell is cast for the rest of the game, the casting number is reduced by 3. A spellcaster may only have one Power Word in effect at a time.',
  },
  {
    id: 'sigilist-push',
    name: 'Push',
    school: 'Sigilist',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'The target is pushed directly away from the spellcaster by 1" per point by which the casting roll exceeded the casting number. If the target hits terrain, they take damage equal to the remaining inches.',
  },
  {
    id: 'sigilist-write-scroll',
    name: 'Write Scroll',
    school: 'Sigilist',
    castingNumber: 12,
    category: 'Out of Game',
    description:
      'The spellcaster creates a scroll of one spell they know. The scroll is a one-use item that allows any figure to cast the spell.',
  },

  // =========================================================================
  // SOOTHSAYER
  // =========================================================================
  {
    id: 'soothsayer-awareness',
    name: 'Awareness',
    school: 'Soothsayer',
    castingNumber: 12,
    category: 'Out of Game',
    description:
      'The spellcaster is forewarned about treasure. The warband may reroll the placement of one treasure token before the game begins.',
  },
  {
    id: 'soothsayer-combat-awareness',
    name: 'Combat Awareness',
    school: 'Soothsayer',
    castingNumber: 12,
    category: 'Touch',
    description:
      'Target figure gains +1 Fight, +1 Shoot, and +1 Armour for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'soothsayer-mind-control',
    name: 'Mind Control',
    school: 'Soothsayer',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The target must make a Will roll (TN = casting roll). If failed, the spellcaster takes control of the target for one activation. The target then makes another Will roll. If failed, control continues.',
  },
  {
    id: 'soothsayer-mind-lock',
    name: 'Mind Lock',
    school: 'Soothsayer',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The target enemy spellcaster must make a Will roll (TN = casting roll). If failed, the target cannot cast spells this turn.',
  },
  {
    id: 'soothsayer-reveal-secret',
    name: 'Reveal Secret',
    school: 'Soothsayer',
    castingNumber: 12,
    category: 'Out of Game',
    description:
      'The spellcaster learns the location of hidden treasure. The warband receives +50gc after the game.',
  },
  {
    id: 'soothsayer-true-sight',
    name: 'True Sight',
    school: 'Soothsayer',
    castingNumber: 10,
    category: 'Self Only',
    description:
      'The spellcaster can see invisible figures and is immune to Beauty and similar spells. Illusions are revealed. Lasts for the rest of the game.',
  },
  {
    id: 'soothsayer-will-power',
    name: 'Will Power',
    school: 'Soothsayer',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target figure gains +3 Will for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'soothsayer-wizard-eye',
    name: 'Wizard Eye',
    school: 'Soothsayer',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'The spellcaster places a magical eye anywhere within line of sight. The spellcaster may draw line of sight from this eye for spell casting purposes. The eye remains for the rest of the game.',
  },

  // =========================================================================
  // SUMMONER
  // =========================================================================
  {
    id: 'summoner-bind-demon',
    name: 'Bind Demon',
    school: 'Summoner',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target demon must make a Will roll (TN = casting roll). If failed, the demon becomes a permanent member of the warband (counts as a specialist soldier).',
  },
  {
    id: 'summoner-control-demon',
    name: 'Control Demon',
    school: 'Summoner',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The target demon must make a Will roll (TN = casting roll). If failed, the spellcaster takes control of the demon for the rest of the game.',
  },
  {
    id: 'summoner-imp',
    name: 'Imp',
    school: 'Summoner',
    castingNumber: 10,
    category: 'Touch',
    description:
      'The spellcaster summons an imp to appear within 1". The imp is a member of the warband for the rest of the game. A spellcaster may have a maximum of one imp at a time.',
  },
  {
    id: 'summoner-leap',
    name: 'Leap',
    school: 'Summoner',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target figure may immediately make a 10" move in any direction, including up. This move may not take the figure within 1" of an enemy. It does not count as an action.',
  },
  {
    id: 'summoner-plague-of-insects',
    name: 'Plague of Insects',
    school: 'Summoner',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target figure is surrounded by a swarm of biting insects. The target suffers -4 Fight and -4 Shoot. At the end of each turn, the target may make a Will roll (TN12) to dispel the insects.',
  },
  {
    id: 'summoner-planar-tear',
    name: 'Planar Tear',
    school: 'Summoner',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'A rift to another plane opens. Place a marker within line of sight. All figures within 1" of the marker (including friendlies) suffer a +2 attack. The rift remains for the rest of the game.',
  },
  {
    id: 'summoner-possess',
    name: 'Possess',
    school: 'Summoner',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'The spellcaster summons a demon to possess a target figure. The target gains +2 Fight and +1 Armour but suffers -2 Will and -2 Shoot. At the end of each activation, the target takes 1 damage.',
  },
  {
    id: 'summoner-summon-demon',
    name: 'Summon Demon',
    school: 'Summoner',
    castingNumber: 12,
    category: 'Touch',
    description:
      'The spellcaster summons a demon (minor demon, imp, or major demon depending on the casting roll) to appear within 1". The demon is uncontrolled unless a Control Demon spell is cast.',
  },

  // =========================================================================
  // THAUMATURGE
  // =========================================================================
  {
    id: 'thaumaturge-banish',
    name: 'Banish',
    school: 'Thaumaturge',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target demon or undead creature must make a Will roll (TN = casting roll). If failed, the creature is removed from the game.',
  },
  {
    id: 'thaumaturge-blinding-light',
    name: 'Blinding Light',
    school: 'Thaumaturge',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target figure must make a Will roll (TN = casting roll). If failed, the target has -1 to all die rolls until the end of the next turn.',
  },
  {
    id: 'thaumaturge-circle-of-protection',
    name: 'Circle of Protection',
    school: 'Thaumaturge',
    castingNumber: 12,
    category: 'Touch',
    description:
      'Creates a circle 3" in diameter. Demons and undead may not enter or attack across the circle. The circle lasts until the end of the game or until the spellcaster moves.',
  },
  {
    id: 'thaumaturge-dispel',
    name: 'Dispel',
    school: 'Thaumaturge',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'Cancels any one spell currently in effect within line of sight.',
  },
  {
    id: 'thaumaturge-heal',
    name: 'Heal',
    school: 'Thaumaturge',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target living figure regains 5 Health, up to their maximum. This spell has no effect on undead or constructs.',
  },
  {
    id: 'thaumaturge-miraculous-cure',
    name: 'Miraculous Cure',
    school: 'Thaumaturge',
    castingNumber: 14,
    category: 'Out of Game',
    description:
      'Target soldier in the warband who was killed recovers from their injuries and returns to the warband at full Health. This spell cannot be used on the wizard or apprentice.',
  },
  {
    id: 'thaumaturge-restore-life',
    name: 'Restore Life',
    school: 'Thaumaturge',
    castingNumber: 20,
    category: 'Out of Game',
    description:
      'Target figure in the warband who was killed is resurrected and returns at full Health. This spell may be used on the wizard or apprentice, but a new apprentice may not be hired until after this spell is attempted.',
  },
  {
    id: 'thaumaturge-shield',
    name: 'Shield',
    school: 'Thaumaturge',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Target figure gains +2 Armour for the rest of the game. Multiple castings on the same target have no effect.',
  },

  // =========================================================================
  // WITCH
  // =========================================================================
  {
    id: 'witch-animal-companion',
    name: 'Animal Companion',
    school: 'Witch',
    castingNumber: 10,
    category: 'Out of Game',
    description:
      'The spellcaster bonds with an animal companion (bear, wolf, ice toad, etc.). The animal joins the warband as a permanent specialist soldier.',
  },
  {
    id: 'witch-brew-potion',
    name: 'Brew Potion',
    school: 'Witch',
    castingNumber: 12,
    category: 'Out of Game',
    description:
      'The spellcaster creates a potion that replicates the effect of one spell they know. The potion is a one-use item that any figure may drink to gain the spell\'s benefit.',
  },
  {
    id: 'witch-control-animal',
    name: 'Control Animal',
    school: 'Witch',
    castingNumber: 12,
    category: 'Line of Sight',
    description:
      'Target animal must make a Will roll (TN = casting roll). If failed, the spellcaster takes control of the animal for the rest of the game.',
  },
  {
    id: 'witch-curse',
    name: 'Curse',
    school: 'Witch',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Target figure suffers -2 to all die rolls for the rest of the game. Multiple castings on the same target have no effect.',
  },
  {
    id: 'witch-familiar',
    name: 'Familiar',
    school: 'Witch',
    castingNumber: 10,
    category: 'Out of Game',
    description:
      'The spellcaster binds a familiar (small animal). The familiar has no combat ability, but the spellcaster gains +2 Health while the familiar is alive. If the familiar dies, it may be replaced.',
  },
  {
    id: 'witch-fog',
    name: 'Fog',
    school: 'Witch',
    castingNumber: 8,
    category: 'Line of Sight',
    description:
      'Places a 6" diameter cloud of fog anywhere within line of sight. Figures may move through the fog, but line of sight may not be drawn through it. The fog remains until the end of the game.',
  },
  {
    id: 'witch-mud',
    name: 'Mud',
    school: 'Witch',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'Creates a 3" diameter area of mud within line of sight. Any figure moving through the mud halves their movement while in the area. The mud remains until the end of the game.',
  },
  {
    id: 'witch-poison-dart',
    name: 'Poison Dart',
    school: 'Witch',
    castingNumber: 10,
    category: 'Line of Sight',
    description:
      'The spellcaster makes a +3 shooting attack against any figure within line of sight. If the attack deals damage, the target also suffers Poison (additional -2 to all die rolls until healed).',
  },
];

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Get all spells for a specific school.
 */
export function getSpellsBySchool(school: WizardSchool): Spell[] {
  return SPELLS.filter((spell) => spell.school === school);
}

/**
 * Get a spell by its ID.
 */
export function getSpellById(id: string): Spell | undefined {
  return SPELLS.find((spell) => spell.id === id);
}

/**
 * Get spells by multiple IDs.
 */
export function getSpellsByIds(ids: string[]): Spell[] {
  return ids.map((id) => getSpellById(id)).filter((s): s is Spell => s !== undefined);
}

/**
 * Calculate casting number for apprentice (base +2).
 */
export function getApprenticeCastingNumber(spell: Spell): number {
  return spell.castingNumber + 2;
}

/**
 * Get casting number modifier for a spell based on wizard's school.
 * Own school: +0, Aligned: +2, Neutral: +4, Opposed: +6
 */
export function getCastingModifier(spell: Spell, wizardSchool: WizardSchool): number {
  if (spell.school === wizardSchool) return 0;

  const relationships = SCHOOL_RELATIONSHIPS[wizardSchool];
  if (relationships.aligned.includes(spell.school)) return 2;
  if (spell.school === relationships.opposed) return 6;
  return 4; // Neutral
}

/**
 * Get effective casting number for a wizard casting a spell.
 */
export function getEffectiveCastingNumber(
  spell: Spell,
  wizardSchool: WizardSchool,
  isApprentice: boolean = false
): number {
  const base = spell.castingNumber;
  const schoolModifier = getCastingModifier(spell, wizardSchool);
  const apprenticeModifier = isApprentice ? 2 : 0;
  return base + schoolModifier + apprenticeModifier;
}

/**
 * Get all unique schools.
 */
export function getAllSchools(): WizardSchool[] {
  return [
    'Chronomancer',
    'Elementalist',
    'Enchanter',
    'Illusionist',
    'Necromancer',
    'Sigilist',
    'Soothsayer',
    'Summoner',
    'Thaumaturge',
    'Witch',
  ];
}

/**
 * Get category display color.
 */
export function getCategoryColor(category: SpellCategory): string {
  switch (category) {
    case 'Self Only':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'Line of Sight':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'Out of Game':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'Touch':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Area Effect':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

/**
 * Get school display color.
 */
export function getSchoolColor(school: WizardSchool): string {
  const colors: Record<WizardSchool, string> = {
    Chronomancer: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    Elementalist: 'bg-red-500/20 text-red-400 border-red-500/30',
    Enchanter: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    Illusionist: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    Necromancer: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    Sigilist: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    Soothsayer: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    Summoner: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    Thaumaturge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    Witch: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
  };
  return colors[school];
}

